import { createContext, useContext, useMemo, ReactNode } from 'react'
import { useVisiblePolling } from '@/hooks/useVisiblePolling'
import { zanshinService, ZanshinPair, ZanshinPairsStatusResponse } from '@/services/zanshin.service'

/**
 * Context condiviso per lo stato Zanshin dell'utente.
 *
 * Un singolo polling visibility-aware per sessione (via useVisiblePolling),
 * condiviso a tutti i consumer. Sostituisce 2+ polling paralleli verso lo
 * stesso endpoint quando piu' componenti coabitano in dashboard.
 *
 * Gating: il prop `enabled` del Provider abilita/disabilita il polling
 * (es. utenti senza subscription -> enabled=false -> zero traffico).
 *
 * Pattern garantiti (eredita da useVisiblePolling):
 *  - polling stop quando il tab non e' visibile, restart al ritorno
 *  - cleanup su unmount
 *  - enabled=false ferma tutto
 */
interface ZanshinContextValue {
  data: ZanshinPairsStatusResponse | null
  error: Error | null
  isLoading: boolean
  /** Map<pair_id, ZanshinPair> per lookup O(1) lato consumer (es. badge inline). */
  pairsByPairId: Map<number, ZanshinPair>
  /** True se il provider e' attivo e sta pollando. False = gated off. */
  enabled: boolean
}

const ZanshinContext = createContext<ZanshinContextValue | null>(null)

export function ZanshinDataProvider({
  enabled,
  intervalMs = 10000,
  children,
}: {
  enabled: boolean
  intervalMs?: number
  children: ReactNode
}) {
  const { data, error, isLoading } = useVisiblePolling(
    zanshinService.getMyPairsStatus,
    intervalMs,
    enabled,
  )

  const pairsByPairId = useMemo(() => {
    const m = new Map<number, ZanshinPair>()
    data?.pairs.forEach(p => m.set(p.pair_id, p))
    return m
  }, [data])

  const value: ZanshinContextValue = { data, error, isLoading, pairsByPairId, enabled }

  return <ZanshinContext.Provider value={value}>{children}</ZanshinContext.Provider>
}

/**
 * Hook consumer. Ritorna un valore safe-default se chiamato fuori dal Provider
 * (utile per pagine non-dashboard che importano componenti Zanshin senza Provider).
 */
export function useZanshinData(): ZanshinContextValue {
  const ctx = useContext(ZanshinContext)
  if (ctx) return ctx
  return {
    data: null,
    error: null,
    isLoading: false,
    pairsByPairId: new Map(),
    enabled: false,
  }
}
